/**
 * The automation engine, without a database or a network — 13 Oct 2026.
 *
 * Three questions, each a pure function of its inputs and a clock:
 *
 *   normaliseRevenueCat  what happened, in the flows' own vocabulary
 *   startRun             does this event start a flow for this person, and when
 *   step                 a run has woken up: what happens next
 *
 * The scheduler (`crmTick`, see automation/runner.ts) supplies the contact and the clock and
 * carries out what `step` asks for; the simulator does the same with a fake
 * clock and made-up profiles; `scripts/checkAutomation.ts` with fixed ones. One
 * engine, so the screen that shows a test run cannot disagree with what the
 * scheduler would have done. See docs/EMAIL_AUTOMATION.md.
 *
 * Like `flows.ts`, this file imports nothing from Node or Firebase.
 */
import type { Block, DomainEvent, Flow, FlowType, Plan, Rule } from './flows.js'
import { TRIAL_SERIES } from './flows.js'

const HOUR = 60 * 60 * 1000
const DAY = 24 * HOUR

/** No time zone on file means Oslo, per the plan. */
export const DEFAULT_TIMEZONE = 'Europe/Oslo'

/** Local hours [from, to) — touring days start early at weekends. */
export const SEND_WINDOW = { weekday: [8, 10], weekend: [7, 9] } as const

/** What the engine needs to know about a person. Built by the contact builder. */
export interface Contact {
  uid: string
  email: string
  firstname: string
  plan: Plan
  autoRenew: boolean
  billingIssue: boolean
  /** `emailSubscription === true` on the product user. */
  marketingConsent: boolean
  /** `emailSubscription === false`: a no, rather than never asked. */
  consentRefused?: boolean
  /** The preference page's choice: 'all' | 'newsletter' | 'automation' | 'none' | ''. */
  emailPreference: string
  timezone: string
  hemisphere: 'north' | 'south'
  lastActiveAtMs: number | null
  /** The latest time each event was seen. */
  events: Partial<Record<DomainEvent, number>>
  /** Automation emails sent, any order. */
  sends: { flow: string; atMs: number; type: FlowType }[]
}

/** One event as the flows understand it. */
export interface EventIn {
  event: DomainEvent
  uid: string
  atMs: number
  /** The end date the event carries: a trial's end, a period's renewal. */
  expiresAtMs?: number | null
  /** Identifies the trial or subscription period, for re-entry. */
  periodKey?: string
}

export interface RunLogLine {
  atMs: number
  text: string
}

/** One person going through one flow, once per re-entry period. */
export interface Run {
  /** `{flow}__{uid}__{period}` — a second start for the same period is a no-op. */
  id: string
  flow: string
  uid: string
  period: string
  startedAtMs: number
  /** The block the run is at. */
  at: string
  wakeAtMs: number
  /** `batch`: waiting for the scheduler to release it in a daily batch. */
  status: 'waiting' | 'batch' | 'done'
  exit: string | null
  log: RunLogLine[]
  /**
   * Let out by the daily batch (stage 5). A released run passes its batch
   * block; its email then counts against the batch budget of 3,500, not the
   * full 4,500, so triggered email always has room.
   */
  released?: boolean
  /**
   * When the subscription or trial that started the run ends, as the event
   * said — 31 Oct 2026, for T03's "only if the trial has 2+ days left". Null
   * when the event had none; absent on runs started before it existed.
   */
  endsAtMs?: number | null
}

export type Action =
  | { kind: 'send'; blockId: string; subject: string; type: FlowType }
  | { kind: 'task'; label: string }

export interface StepResult {
  run: Run
  actions: Action[]
  /**
   * What happened on the canvas, as `{blockId}__{metric}` — a condition's
   * `yes`/`no`, an exit's `exited`, a task's `created`, a batch's `batched`. The
   * scheduler adds these to the day's counters; the send's `sent` is added
   * once Resend has the message, not here.
   */
  tally: string[]
  /** Every block this step ran, in order — the simulator draws the path with it. */
  visited: string[]
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** The fields of a RevenueCat webhook event this reads. */
export interface RcEventLike {
  type?: string
  app_user_id?: string
  original_app_user_id?: string
  period_type?: string
  is_trial_conversion?: boolean
  expiration_at_ms?: number | null
  event_timestamp_ms?: number
  original_transaction_id?: string | null
}

/**
 * A RevenueCat event, as the flows' events.
 *
 * The mapping the plan settled on: a trial is an INITIAL_PURCHASE with
 * period_type TRIAL; it converts on a RENEWAL with is_trial_conversion and
 * expires on an EXPIRATION while still a trial. A conversion is also the start
 * of a paid subscription, so it yields both events — T10 welcomes everybody who
 * starts paying, however they got there.
 */
export function normaliseRevenueCat(e: RcEventLike): EventIn[] {
  const uid = e.app_user_id ?? e.original_app_user_id
  if (!uid || !e.type) return []
  const atMs = e.event_timestamp_ms ?? 0
  const expiresAtMs = typeof e.expiration_at_ms === 'number' ? e.expiration_at_ms : null
  const trial = e.period_type === 'TRIAL'
  const period = (prefix: string) =>
    `${prefix}${e.original_transaction_id ?? ''}${expiresAtMs ?? atMs}`.replace(/[^A-Za-z0-9_-]/g, '')
  const ev = (event: DomainEvent, periodKey: string): EventIn => ({ event, uid, atMs, expiresAtMs, periodKey })

  switch (e.type) {
    case 'INITIAL_PURCHASE':
      return trial ? [ev('trial.started', period('t'))] : [ev('subscription.started', period('s'))]
    case 'RENEWAL':
      return e.is_trial_conversion
        ? [ev('trial.converted', period('t')), ev('subscription.started', period('s'))]
        : [ev('subscription.renewed', period('s'))]
    case 'CANCELLATION':
      return [ev('subscription.cancelled', period('s'))]
    case 'UNCANCELLATION':
      return [ev('subscription.uncancelled', period('s'))]
    case 'EXPIRATION':
      return trial ? [ev('trial.expired', period('t'))] : [ev('subscription.expired', period('s'))]
    case 'BILLING_ISSUE':
      return [ev('billing.issue', period('s'))]
    default:
      return []
  }
}

// ---------------------------------------------------------------------------
// Starting a run
// ---------------------------------------------------------------------------

/**
 * The season a moment belongs to, as a key: `N2026` is the northern season
 * that starts in late 2026, `S2027` the southern one that starts in 2027.
 */
export function seasonKey(atMs: number, hemisphere: 'north' | 'south'): string {
  const d = new Date(atMs)
  const y = d.getUTCFullYear()
  const m = d.getUTCMonth() + 1
  // North runs roughly Nov–May: Jan–Jul still belongs to the season that began
  // the previous autumn. South runs roughly May–Oct within one year.
  return hemisphere === 'north' ? `N${m >= 8 ? y : y - 1}` : `S${y}`
}

const safeId = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_')

/**
 * Whether an event starts this flow for this person, and the run if it does.
 *
 * Returns null when the trigger does not match, or when the moment it anchors on
 * has already passed — a "trial ends tomorrow" for a trial that ended is not
 * something to send late.
 */
export function startRun(
  flow: Flow,
  ev: EventIn,
  nowMs: number,
  hemisphere: 'north' | 'south' = 'north',
): Run | null {
  const t = flow.trigger
  if (t.kind !== 'event' || t.event !== ev.event) return null

  const anchor = t.from === 'expiresAt' ? ev.expiresAtMs : ev.atMs
  if (typeof anchor !== 'number' || !Number.isFinite(anchor) || anchor <= 0) return null
  if (t.from === 'expiresAt' && anchor <= nowMs) return null
  const wake = anchor + (t.offsetHours ?? 0) * HOUR

  const period =
    flow.reentry === 'user'
      ? 'once'
      : flow.reentry === 'season'
        ? seasonKey(ev.atMs, hemisphere)
        : (ev.periodKey ?? String(ev.expiresAtMs ?? ev.atMs))

  return {
    id: safeId(`${flow.id}__${ev.uid}__${period}`),
    flow: flow.id,
    uid: ev.uid,
    period,
    startedAtMs: nowMs,
    at: flow.blocks[0]!.id,
    wakeAtMs: Math.max(wake, nowMs),
    status: 'waiting',
    exit: null,
    log: [{ atMs: nowMs, text: `Started by "${ev.event}"` }],
    endsAtMs: typeof ev.expiresAtMs === 'number' ? ev.expiresAtMs : null,
  }
}

// ---------------------------------------------------------------------------
// Time: the send window
// ---------------------------------------------------------------------------

interface LocalParts {
  y: number
  m: number
  d: number
  h: number
  mi: number
  weekend: boolean
}

function localParts(ms: number, tz: string): LocalParts {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  })
  const p: Record<string, string> = {}
  for (const x of f.formatToParts(new Date(ms))) p[x.type] = x.value
  return {
    y: Number(p.year),
    m: Number(p.month),
    d: Number(p.day),
    h: Number(p.hour),
    mi: Number(p.minute),
    weekend: p.weekday === 'Sat' || p.weekday === 'Sun',
  }
}

/** The UTC instant of a wall-clock time in a zone, DST included. */
function zonedToUtc(y: number, m: number, d: number, h: number, tz: string): number {
  const guess = Date.UTC(y, m - 1, d, h, 0)
  const offsetAt = (ms: number) => {
    const p = localParts(ms, tz)
    return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi) - Math.floor(ms / 60000) * 60000
  }
  const first = guess - offsetAt(guess)
  return guess - offsetAt(first)
}

function validZone(tz: string): string {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz })
    return tz
  } catch {
    return DEFAULT_TIMEZONE
  }
}

/**
 * The first moment at or after `nowMs` inside the send window, in the
 * contact's own time. Already inside it means now.
 */
export function nextSendTime(nowMs: number, timezone: string): number {
  const tz = validZone(timezone || DEFAULT_TIMEZONE)
  const today = localParts(nowMs, tz)
  for (let n = 0; n < 9; n++) {
    const day = new Date(Date.UTC(today.y, today.m - 1, today.d + n, 12))
    const y = day.getUTCFullYear()
    const m = day.getUTCMonth() + 1
    const d = day.getUTCDate()
    const weekend = day.getUTCDay() === 0 || day.getUTCDay() === 6
    const [from, to] = weekend ? SEND_WINDOW.weekend : SEND_WINDOW.weekday
    const start = zonedToUtc(y, m, d, from, tz)
    const end = zonedToUtc(y, m, d, to, tz)
    if (nowMs < end) return Math.max(nowMs, start)
  }
  return nowMs + DAY
}

// ---------------------------------------------------------------------------
// The frequency cap and consent
// ---------------------------------------------------------------------------

/** At most 1 lifecycle or marketing email a day, and 2 in any 7 days. */
export const CAP = { perDay: 1, perWeek: 2 } as const

/**
 * When this person may next get a capped email. `nowMs` when they may now.
 *
 * Rolling windows rather than calendar days, for the same reason the sending
 * budget is rolling: 23:59 and 00:01 are not two days apart to a reader.
 *
 * The trial series (T01, T03, T05 — `trialSeries` in flows.ts) is held to the
 * daily cap only, and its emails are not counted in anybody's weekly cap —
 * 31 Oct 2026. A seven-day trial sends on days 0, 3 and 6; with the weekly
 * cap the third would wait until after the trial had ended, and T06 the day
 * after the end would wait too. Never two in a day still holds for all of it.
 */
export function nextCapTime(sends: Contact['sends'], nowMs: number, flow?: Pick<Flow, 'trialSeries'>): number {
  const capped = sends.filter((s) => s.type !== 'transactional').sort((a, b) => b.atMs - a.atMs)
  const inDay = capped.map((s) => s.atMs).filter((t) => t > nowMs - DAY)
  const inWeek = capped
    .filter((s) => !TRIAL_SERIES.has(s.flow))
    .map((s) => s.atMs)
    .filter((t) => t > nowMs - 7 * DAY)
  let at = nowMs
  if (inDay.length >= CAP.perDay) at = Math.max(at, inDay[CAP.perDay - 1]! + DAY)
  if (!flow?.trialSeries && inWeek.length >= CAP.perWeek) at = Math.max(at, inWeek[CAP.perWeek - 1]! + 7 * DAY)
  return at
}

/**
 * Whether this person has agreed to this email at all.
 *
 * Transactional mail goes regardless, as a receipt does. Everything else needs
 * a yes, and the preference page's choice must admit the flow's stream — the
 * same rule `admits()` applies to newsletters.
 */
export function consents(c: Contact, flow: Flow): boolean {
  if (flow.type === 'transactional') return true
  const p = c.emailPreference || 'all'
  if (p === 'none') return false
  if (flow.unlessRefused) return !c.consentRefused
  if (!c.marketingConsent) return false
  if (p === 'all') return true
  return p === flow.stream
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

export function ruleHolds(r: Rule, c: Contact, run: Run, nowMs: number): boolean {
  switch (r.field) {
    case 'plan':
      return r.op === 'is' ? c.plan === r.value : c.plan !== r.value
    case 'autoRenew':
    case 'billingIssue':
    case 'marketingConsent':
      return c[r.field] === r.value
    case 'event': {
      const seen = c.events[r.value]
      return seen === undefined || seen < run.startedAtMs
    }
    case 'sent':
      return !c.sends.some((s) => s.flow === r.value && nowMs - s.atMs < r.days * DAY)
    case 'inactiveDays': {
      // Unknown is not inactive — 22 Oct 2026. No source writes last-active
      // yet, and treating it as "away forever" would send T20's dormant email
      // to everybody. Neither side of the check holds until it is known.
      if (c.lastActiveAtMs === null) return false
      const days = (nowMs - c.lastActiveAtMs) / DAY
      return r.op === 'at-least' ? days >= r.value : days <= r.value
    }
    case 'trialEndsInDays': {
      // Unknown end: holds. A run from before the field existed, or an event
      // without an expiry — sending is what the flow did before the rule.
      if (typeof run.endsAtMs !== 'number') return true
      return (run.endsAtMs - nowMs) / DAY >= r.value
    }
  }
}

// ---------------------------------------------------------------------------
// Stepping a run
// ---------------------------------------------------------------------------

function blockAfter(flow: Flow, id: string): Block | undefined {
  const i = flow.blocks.findIndex((b) => b.id === id)
  return i >= 0 ? flow.blocks[i + 1] : undefined
}

/**
 * Move a run that has woken up, as far as it can go right now.
 *
 * Instant blocks (conditions, a send window already open, a task) run one after
 * another; the run stops at a wait in the future, a batch, an email to send, or
 * an exit. An email is **not** marked sent here: the scheduler sends it, and
 * only then calls `markSent`, so a failed send leaves the run where it was.
 */
export function step(input: Run, flow: Flow, c: Contact, nowMs: number): StepResult {
  const run: Run = { ...input, log: [...input.log] }
  const actions: Action[] = []
  const tally: string[] = []
  const visited: string[] = []
  const note = (text: string) => run.log.push({ atMs: nowMs, text })
  const go = (id: string | undefined) => {
    if (!id) {
      run.status = 'done'
      run.exit = 'Reached the end of the flow'
      return false
    }
    run.at = id
    return true
  }

  if (run.status === 'done' || run.wakeAtMs > nowMs) return { run, actions, tally, visited }
  run.status = 'waiting'

  // Premium comes with Skida PRO: the emails about buying it are not for a
  // member. Checked when the run wakes, so somebody who joins a company
  // mid-trial is caught too.
  if (flow.notForPro && c.plan === 'pro') {
    run.status = 'done'
    run.exit = 'Skida PRO member — Premium comes with PRO'
    note(run.exit)
    return { run, actions, tally, visited }
  }

  for (let guard = 0; guard < flow.blocks.length + 2; guard++) {
    const b = flow.blocks.find((x) => x.id === run.at)
    if (!b) {
      run.status = 'done'
      run.exit = `Unknown block "${run.at}"`
      note(run.exit)
      return { run, actions, tally, visited }
    }
    visited.push(b.id)

    switch (b.kind) {
      case 'wait': {
        const next = blockAfter(flow, b.id)?.id
        if ('hours' in b) {
          if (!go(next)) return { run, actions, tally, visited }
          run.wakeAtMs = nowMs + b.hours * HOUR
          note(`Waiting ${b.hours} h`)
          return { run, actions, tally, visited }
        }
        const at = nextSendTime(nowMs, c.timezone)
        if (!go(next)) return { run, actions, tally, visited }
        if (at > nowMs) {
          run.wakeAtMs = at
          note(`Waiting for the send window${c.timezone ? '' : ` (${DEFAULT_TIMEZONE})`}`)
          return { run, actions, tally, visited }
        }
        continue
      }
      case 'condition': {
        const ok = b.rules.every((r) => ruleHolds(r, c, run, nowMs))
        note(`${b.label}: ${ok ? 'yes' : 'no'}`)
        tally.push(`${b.id}__${ok ? 'yes' : 'no'}`)
        if (!go(ok ? blockAfter(flow, b.id)?.id : b.no)) return { run, actions, tally, visited }
        continue
      }
      case 'batch': {
        if (!go(blockAfter(flow, b.id)?.id)) return { run, actions, tally, visited }
        if (run.released) {
          note('Released in a daily batch')
          tally.push(`${b.id}__released`)
          continue
        }
        run.status = 'batch'
        note('Waiting for a batch')
        tally.push(`${b.id}__batched`)
        return { run, actions, tally, visited }
      }
      case 'task': {
        actions.push({ kind: 'task', label: b.label })
        tally.push(`${b.id}__created`)
        note(b.label)
        if (!go(blockAfter(flow, b.id)?.id)) return { run, actions, tally, visited }
        continue
      }
      case 'send': {
        if (!consents(c, flow)) {
          run.status = 'done'
          run.exit = 'No consent for this email'
          note(run.exit)
          return { run, actions, tally, visited }
        }
        if (flow.type !== 'transactional') {
          const capAt = nextCapTime(c.sends, nowMs, flow)
          // Late is worse than never for T03 (a day's wait would put it on the
          // day of the next email in the series), and for a trial email whose
          // trial is over before the cap frees ("ends tomorrow", in a one-day
          // trial that began with the welcome).
          const tooLate = flow.trialSeries === true && typeof run.endsAtMs === 'number' && capAt >= run.endsAtMs
          if (capAt > nowMs && (flow.skipIfCapped || tooLate)) {
            run.status = 'done'
            run.exit = tooLate ? 'The trial ends before another email may go; skipped' : 'Another email in the last 24 hours; skipped'
            note(run.exit)
            tally.push(`${b.id}__skipped`)
            return { run, actions, tally, visited }
          }
          if (capAt > nowMs) {
            // The cap is full: try again when it frees, inside the window.
            run.wakeAtMs = nextSendTime(capAt, c.timezone)
            note('Frequency cap full; waiting')
            return { run, actions, tally, visited }
          }
        }
        actions.push({ kind: 'send', blockId: b.id, subject: b.subject, type: flow.type })
        return { run, actions, tally, visited }
      }
      case 'exit': {
        run.status = 'done'
        run.exit = b.reason
        note(`Exit: ${b.reason}`)
        tally.push(`${b.id}__exited`)
        return { run, actions, tally, visited }
      }
    }
  }
  run.status = 'done'
  run.exit = 'Stopped: the flow loops'
  return { run, actions, tally, visited }
}

/** The scheduler sent the email at `run.at`: move on past it. */
export function markSent(input: Run, flow: Flow, nowMs: number): Run {
  const run: Run = { ...input, log: [...input.log, { atMs: nowMs, text: 'Sent' }] }
  const next = blockAfter(flow, run.at)?.id
  if (next) {
    run.at = next
    run.wakeAtMs = nowMs
  } else {
    run.status = 'done'
    run.exit = 'Email sent'
  }
  return run
}
